import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, money, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip } from "recharts";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, Download, Check, X, Upload } from "lucide-react";

const STATUSES = ["pending", "processing", "shipped", "delivered", "cancelled"];
const EMPTY_P = { name: "", slug: "", category: "Wallets", group: "Small Leather Goods", gender: "unisex", tags: [],
  price: 0, compare_price: null, stock: 10, description: "", material: "", care: "", images: [], featured: false, active: true };
const EMPTY_B = { title: "", slug: "", excerpt: "", body: "", cover_image: "", author: "Trust Luxa", published: true };

const Field = ({ label, testid, ...rest }) => (
  <div>
    <Label className="overline text-luxa-espresso/50">{label}</Label>
    <Input data-testid={testid} className="mt-2 rounded-none bg-luxa-shell border-luxa-sand h-11" {...rest} />
  </div>
);

const ImageUploader = ({ value, onChange, testid }) => {
  const [busy, setBusy] = useState(false);
  const urls = Array.isArray(value) ? value : String(value || "").split(",").map((s) => s.trim()).filter(Boolean);

  const upload = async (files) => {
    setBusy(true);
    try {
      const uploaded = [];
      for (const file of Array.from(files)) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(data.url);
      }
      onChange([...urls, ...uploaded]);
      toast.success(`${uploaded.length} image${uploaded.length === 1 ? "" : "s"} uploaded`);
    } catch (e) { toast.error(errMsg(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <Label className="overline text-luxa-espresso/50">Images</Label>
      <div className="mt-2 flex flex-wrap gap-3">
        {urls.map((u, i) => (
          <div key={`${u}-${i}`} className="relative group">
            <img src={u} alt="" className="w-16 h-20 object-cover border border-luxa-sand" />
            <button type="button" data-testid={`${testid}-remove-${i}`} onClick={() => onChange(urls.filter((_, k) => k !== i))}
              className="absolute -top-2 -right-2 bg-luxa-espresso text-luxa-cream w-5 h-5 flex items-center justify-center">
              <X size={11} strokeWidth={2} />
            </button>
          </div>
        ))}
        <label data-testid={`${testid}-dropzone`}
          className="w-16 h-20 border border-dashed border-luxa-espresso/30 flex flex-col items-center justify-center cursor-pointer hover:border-luxa-gold transition-colors duration-300">
          <Upload size={14} strokeWidth={1.5} />
          <span className="text-[9px] tracking-widest uppercase mt-1">{busy ? "…" : "Add"}</span>
          <input data-testid={`${testid}-input`} type="file" accept="image/*" multiple className="hidden"
            onChange={(e) => e.target.files?.length && upload(e.target.files)} />
        </label>
      </div>
      <Textarea data-testid={testid} rows={2} className="mt-3 rounded-none bg-luxa-shell border-luxa-sand text-xs"
        placeholder="Or paste image URLs, comma separated"
        value={urls.join(", ")} onChange={(e) => onChange(e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} />
      <p className="mt-2 text-xs text-luxa-espresso/45">Upload your own photography — JPG, PNG or WEBP up to 10MB. First image is the cover.</p>
    </div>
  );
};

const ProductDialog = ({ initial, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(initial || EMPTY_P);
  const editing = Boolean(initial?.id);
  const save = async () => {
    const payload = { ...f, price: Number(f.price), stock: Number(f.stock),
      compare_price: f.compare_price ? Number(f.compare_price) : null,
      tags: typeof f.tags === "string" ? f.tags.split(",").map((s) => s.trim()).filter(Boolean) : f.tags,
      images: typeof f.images === "string" ? f.images.split(",").map((s) => s.trim()).filter(Boolean) : f.images,
      slug: f.slug || f.name.toLowerCase().replace(/[^a-z0-9]+/g, "-") };
    try {
      if (editing) await api.put(`/admin/products/${initial.id}`, payload);
      else await api.post("/admin/products", payload);
      toast.success(editing ? "Product updated" : "Product created");
      setOpen(false); onSaved();
    } catch (e) { toast.error(errMsg(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) setF(initial || EMPTY_P); }}>
      <DialogTrigger asChild>
        {editing
          ? <button data-testid={`edit-product-${initial.id}`} className="text-luxa-espresso/60 hover:text-luxa-gold"><Pencil size={15} strokeWidth={1.5} /></button>
          : <Button data-testid="new-product-button" className="rounded-none bg-luxa-brown text-white h-11 overline"><Plus size={14} className="mr-2" />New Product</Button>}
      </DialogTrigger>
      <DialogContent className="bg-luxa-cream max-w-2xl max-h-[88vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-serif text-2xl font-normal">{editing ? "Edit product" : "New product"}</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Name" testid="product-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
          <Field label="Slug" testid="product-slug" value={f.slug} onChange={(e) => setF({ ...f, slug: e.target.value })} />
          <Field label="Category" testid="product-category" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} />
          <Field label="Group (Bags / Small Leather Goods / Shoes / Leather Clothing / Travel)" testid="product-group"
            value={f.group || ""} onChange={(e) => setF({ ...f, group: e.target.value })} />
          <Field label="Gender (men / women / unisex)" testid="product-gender"
            value={f.gender || ""} onChange={(e) => setF({ ...f, gender: e.target.value })} />
          <Field label="Tags (new-arrivals, best-sellers, travel, business, gifts)" testid="product-tags"
            value={Array.isArray(f.tags) ? f.tags.join(", ") : f.tags || ""} onChange={(e) => setF({ ...f, tags: e.target.value })} />
          <Field label="Price (USD)" testid="product-price" type="number" step="0.01" value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} />
          <Field label="Compare price" testid="product-compare" type="number" step="0.01" value={f.compare_price ?? ""} onChange={(e) => setF({ ...f, compare_price: e.target.value })} />
          <Field label="Stock" testid="product-stock" type="number" value={f.stock} onChange={(e) => setF({ ...f, stock: e.target.value })} />
          <div className="sm:col-span-2">
            <ImageUploader testid="product-images" value={f.images} onChange={(v) => setF({ ...f, images: v })} />
          </div>
          <div className="sm:col-span-2">
            <Label className="overline text-luxa-espresso/50">Description</Label>
            <Textarea data-testid="product-description" rows={3} className="mt-2 rounded-none bg-luxa-shell border-luxa-sand"
              value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />
          </div>
          <div className="sm:col-span-2">
            <Label className="overline text-luxa-espresso/50">Material</Label>
            <Textarea data-testid="product-material" rows={2} className="mt-2 rounded-none bg-luxa-shell border-luxa-sand"
              value={f.material} onChange={(e) => setF({ ...f, material: e.target.value })} />
          </div>
          <div className="sm:col-span-2">
            <Label className="overline text-luxa-espresso/50">Care guide</Label>
            <Textarea data-testid="product-care" rows={2} className="mt-2 rounded-none bg-luxa-shell border-luxa-sand"
              value={f.care} onChange={(e) => setF({ ...f, care: e.target.value })} />
          </div>
          <label className="overline flex items-center gap-3">
            <input data-testid="product-featured" type="checkbox" checked={f.featured} onChange={(e) => setF({ ...f, featured: e.target.checked })} /> Featured
          </label>
          <label className="overline flex items-center gap-3">
            <input data-testid="product-active" type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} /> Active
          </label>
        </div>
        <Button data-testid="save-product-button" onClick={save} className="rounded-none bg-luxa-brown text-white h-12 overline mt-2">Save Product</Button>
      </DialogContent>
    </Dialog>
  );
};

const BlogDialog = ({ initial, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(initial || EMPTY_B);
  const editing = Boolean(initial?.id);
  const save = async () => {
    const payload = { ...f, slug: f.slug || f.title.toLowerCase().replace(/[^a-z0-9]+/g, "-") };
    try {
      if (editing) await api.put(`/admin/blog/${initial.id}`, payload); else await api.post("/admin/blog", payload);
      toast.success("Post saved"); setOpen(false); onSaved();
    } catch (e) { toast.error(errMsg(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) setF(initial || EMPTY_B); }}>
      <DialogTrigger asChild>
        {editing
          ? <button data-testid={`edit-post-${initial.id}`} className="text-luxa-espresso/60 hover:text-luxa-gold"><Pencil size={15} strokeWidth={1.5} /></button>
          : <Button data-testid="new-post-button" className="rounded-none bg-luxa-brown text-white h-11 overline"><Plus size={14} className="mr-2" />New Post</Button>}
      </DialogTrigger>
      <DialogContent className="bg-luxa-cream max-w-2xl max-h-[88vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-serif text-2xl font-normal">{editing ? "Edit post" : "New post"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <Field label="Title" testid="post-title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} />
          <Field label="Slug" testid="post-slug" value={f.slug} onChange={(e) => setF({ ...f, slug: e.target.value })} />
          <ImageUploader testid="post-cover" value={f.cover_image ? [f.cover_image] : []}
            onChange={(v) => setF({ ...f, cover_image: v[v.length - 1] || "" })} />
          <div>
            <Label className="overline text-luxa-espresso/50">Excerpt</Label>
            <Textarea data-testid="post-excerpt" rows={2} className="mt-2 rounded-none bg-luxa-shell border-luxa-sand" value={f.excerpt} onChange={(e) => setF({ ...f, excerpt: e.target.value })} />
          </div>
          <div>
            <Label className="overline text-luxa-espresso/50">Body</Label>
            <Textarea data-testid="post-body" rows={8} className="mt-2 rounded-none bg-luxa-shell border-luxa-sand" value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} />
          </div>
          <label className="overline flex items-center gap-3">
            <input data-testid="post-published" type="checkbox" checked={f.published} onChange={(e) => setF({ ...f, published: e.target.checked })} /> Published
          </label>
          <Button data-testid="save-post-button" onClick={save} className="w-full rounded-none bg-luxa-brown text-white h-12 overline">Save Post</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default function Admin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [posts, setPosts] = useState([]);
  const [subs, setSubs] = useState([]);

  const loadAll = useCallback(() => {
    Promise.all([
      api.get("/admin/stats"), api.get("/admin/products"), api.get("/admin/orders"),
      api.get("/admin/customers"), api.get("/admin/reviews"), api.get("/admin/blog"), api.get("/admin/subscribers"),
    ]).then(([s, p, o, c, r, b, sb]) => {
      setStats(s.data); setProducts(p.data); setOrders(o.data); setCustomers(c.data);
      setReviews(r.data); setPosts(b.data); setSubs(sb.data);
    }).catch(() => toast.error("Could not load admin data"));
  }, []);

  useEffect(() => {
    if (user === false) navigate("/login");
    else if (user && user.role !== "admin") navigate("/account");
    else if (user) loadAll();
  }, [user, navigate, loadAll]);

  if (!user || user.role !== "admin") return <div className="py-32 text-center overline text-luxa-espresso/40">Loading…</div>;

  const setStatus = async (id, status) => {
    try { await api.put(`/admin/orders/${id}/status`, { status }); toast.success(`Order ${id} → ${status}`); loadAll(); }
    catch (e) { toast.error(errMsg(e.response?.data?.detail)); }
  };
  const delProduct = async (id) => { await api.delete(`/admin/products/${id}`); toast.success("Product deleted"); loadAll(); };
  const moderate = async (id, approved) => { await api.put(`/admin/reviews/${id}?approved=${approved}`); toast.success(approved ? "Approved" : "Rejected"); loadAll(); };
  const delPost = async (id) => { await api.delete(`/admin/blog/${id}`); toast.success("Post deleted"); loadAll(); };
  const exportSubs = () => {
    const csv = "email,created_at\n" + subs.map((s) => `${s.email},${s.created_at}`).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = "trust-luxa-subscribers.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const cards = stats ? [["Revenue", money(stats.revenue)], ["Orders", stats.orders], ["Customers", stats.customers],
    ["Products", stats.products], ["Low stock", `${(stats.low_stock || []).length}`], ["Reviews pending", stats.pending_reviews]] : [];

  return (
    <div className="max-w-[1500px] mx-auto px-5 sm:px-6 py-10" data-testid="admin-page">
      <p className="overline text-luxa-brown">Admin</p>
      <h1 className="mt-3 font-serif text-3xl sm:text-4xl tracking-tight">Trust Luxa Control Room</h1>

      <div className="mt-8 grid grid-cols-2 lg:grid-cols-6 gap-4">
        {cards.map(([l, v]) => (
          <div key={l} className="border border-luxa-sand bg-luxa-shell p-5" data-testid={`stat-${l.split(" ")[0].toLowerCase()}`}>
            <p className="overline text-luxa-espresso/45">{l}</p>
            <p className="mt-2 font-serif text-2xl">{v}</p>
          </div>
        ))}
      </div>

      {stats?.low_stock?.length > 0 && (
        <div className="mt-6 border border-luxa-gold/60 bg-luxa-shell p-5" data-testid="low-stock-alert">
          <p className="overline text-luxa-espresso/60">
            Running low — {stats.low_stock_threshold} or fewer in stock
            {stats.out_of_stock > 0 && ` · ${stats.out_of_stock} sold out`}
          </p>
          <div className="mt-4 flex flex-wrap gap-3">
            {stats.low_stock.map((p) => (
              <span key={p.id} data-testid={`low-stock-${p.id}`}
                className={`overline px-3 py-2 border ${p.stock <= 0 ? "border-red-800/40 text-red-800" : "border-luxa-sand text-luxa-espresso/70"}`}>
                {p.name} · {p.stock <= 0 ? "sold out" : `${p.stock} left`}
              </span>
            ))}
          </div>
        </div>
      )}

      <Tabs defaultValue="orders" className="mt-10">
        <TabsList className="rounded-none bg-transparent border-b border-luxa-sand w-full justify-start gap-6 p-0 h-auto overflow-x-auto">
          {[["orders", "Orders"], ["products", "Products"], ["customers", "Customers"], ["reviews", "Reviews"], ["blog", "Blog"], ["subscribers", "Subscribers"], ["analytics", "Analytics"]].map(([v, l]) => (
            <TabsTrigger key={v} value={v} data-testid={`admin-tab-${v}`}
              className="rounded-none bg-transparent overline whitespace-nowrap data-[state=active]:text-luxa-brown data-[state=active]:shadow-none border-b-2 border-transparent data-[state=active]:border-luxa-brown pb-3 px-0">{l}</TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="orders" className="mt-6">
          <div className="border border-luxa-sand bg-luxa-shell overflow-x-auto">
            <Table>
              <TableHeader><TableRow>{["Order", "Date", "Customer", "Total", "Payment", "Status"].map((h) => <TableHead key={h} className="overline">{h}</TableHead>)}</TableRow></TableHeader>
              <TableBody>
                {orders.map((o) => (
                  <TableRow key={o.id} data-testid={`admin-order-${o.id}`}>
                    <TableCell className="font-medium">{o.id}</TableCell>
                    <TableCell className="text-sm">{o.created_at.slice(0, 10)}</TableCell>
                    <TableCell className="text-sm">{o.email}</TableCell>
                    <TableCell>{money(o.total)}</TableCell>
                    <TableCell className="text-xs uppercase">{o.payment_method} / {o.payment_status}</TableCell>
                    <TableCell>
                      <select data-testid={`order-status-${o.id}`} value={o.status} onChange={(e) => setStatus(o.id, e.target.value)}
                        className="border border-luxa-sand bg-luxa-shell px-2 py-1 text-xs uppercase tracking-wider">
                        {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {orders.length === 0 && <p className="p-6 text-sm text-luxa-espresso/50">No orders yet.</p>}
          </div>
        </TabsContent>

        <TabsContent value="products" className="mt-6">
          <div className="flex justify-end mb-4"><ProductDialog onSaved={loadAll} /></div>
          <div className="border border-luxa-sand bg-luxa-shell overflow-x-auto">
            <Table>
              <TableHeader><TableRow>{["", "Name", "Category", "Price", "Stock", "Flags", ""].map((h, i) => <TableHead key={i} className="overline">{h}</TableHead>)}</TableRow></TableHeader>
              <TableBody>
                {products.map((p) => (
                  <TableRow key={p.id} data-testid={`admin-product-${p.id}`}>
                    <TableCell><img src={p.images?.[0]} alt="" className="w-10 h-12 object-cover" /></TableCell>
                    <TableCell className="font-medium">{p.name}</TableCell>
                    <TableCell className="text-sm">{p.category}</TableCell>
                    <TableCell>{money(p.price)}</TableCell>
                    <TableCell>
                      <span className={p.stock <= 0 ? "text-red-800" : p.stock <= (stats?.low_stock_threshold ?? 3) ? "text-luxa-gold" : ""}>
                        {p.stock <= 0 ? "Sold out" : p.stock}
                      </span>
                    </TableCell>
                    <TableCell className="text-xs uppercase tracking-wider text-luxa-espresso/55">{p.featured ? "featured " : ""}{p.active ? "active" : "hidden"}</TableCell>
                    <TableCell className="flex gap-4 items-center">
                      <ProductDialog initial={p} onSaved={loadAll} />
                      <button data-testid={`delete-product-${p.id}`} onClick={() => delProduct(p.id)} className="text-luxa-espresso/60 hover:text-red-700"><Trash2 size={15} strokeWidth={1.5} /></button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="customers" className="mt-6">
          <div className="border border-luxa-sand bg-luxa-shell overflow-x-auto">
            <Table>
              <TableHeader><TableRow>{["Name", "Email", "Orders", "Joined"].map((h) => <TableHead key={h} className="overline">{h}</TableHead>)}</TableRow></TableHeader>
              <TableBody>
                {customers.map((c) => (
                  <TableRow key={c.user_id} data-testid={`admin-customer-${c.user_id}`}>
                    <TableCell className="font-medium">{c.name}</TableCell>
                    <TableCell className="text-sm">{c.email}</TableCell>
                    <TableCell>{c.order_count}</TableCell>
                    <TableCell className="text-sm">{c.created_at?.slice(0, 10)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="reviews" className="mt-6 space-y-3">
          {reviews.map((r) => (
            <div key={r.id} className="border border-luxa-sand bg-luxa-shell p-5 flex flex-wrap gap-4 items-start" data-testid={`admin-review-${r.id}`}>
              <div className="flex-1 min-w-[240px]">
                <p className="overline text-luxa-espresso/45">{r.author} · {r.rating}★ · {r.approved ? "approved" : "pending"}{r.verified_buyer ? " · verified" : ""}</p>
                <p className="mt-2 font-serif text-lg">{r.title || "(no title)"}</p>
                <p className="mt-1 text-sm text-luxa-espresso/70">{r.body}</p>
              </div>
              <div className="flex gap-2">
                <Button data-testid={`approve-review-${r.id}`} onClick={() => moderate(r.id, true)} className="rounded-none h-10 overline bg-luxa-brown text-white"><Check size={14} className="mr-1" />Approve</Button>
                <Button data-testid={`reject-review-${r.id}`} onClick={() => moderate(r.id, false)} variant="outline" className="rounded-none h-10 overline"><X size={14} className="mr-1" />Reject</Button>
              </div>
            </div>
          ))}
          {reviews.length === 0 && <p className="text-sm text-luxa-espresso/50">No reviews submitted.</p>}
        </TabsContent>

        <TabsContent value="blog" className="mt-6">
          <div className="flex justify-end mb-4"><BlogDialog onSaved={loadAll} /></div>
          <div className="space-y-3">
            {posts.map((p) => (
              <div key={p.id} className="border border-luxa-sand bg-luxa-shell p-5 flex gap-4 items-center" data-testid={`admin-post-${p.id}`}>
                <img src={p.cover_image} alt="" className="w-14 h-14 object-cover" />
                <div className="flex-1">
                  <p className="font-serif text-lg">{p.title}</p>
                  <p className="overline text-luxa-espresso/45 mt-1">{p.published ? "published" : "draft"} · {p.slug}</p>
                </div>
                <BlogDialog initial={p} onSaved={loadAll} />
                <button data-testid={`delete-post-${p.id}`} onClick={() => delPost(p.id)} className="text-luxa-espresso/60 hover:text-red-700"><Trash2 size={15} strokeWidth={1.5} /></button>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="subscribers" className="mt-6">
          <Button data-testid="export-subscribers" onClick={exportSubs} className="rounded-none bg-luxa-brown text-white h-11 overline mb-4">
            <Download size={14} className="mr-2" />Export CSV
          </Button>
          <div className="border border-luxa-sand bg-luxa-shell divide-y divide-luxa-espresso/10">
            {subs.map((s) => <p key={s.email} className="px-5 py-3 text-sm" data-testid={`subscriber-${s.email}`}>{s.email}</p>)}
            {subs.length === 0 && <p className="p-5 text-sm text-luxa-espresso/50">No subscribers yet.</p>}
          </div>
        </TabsContent>

        <TabsContent value="analytics" className="mt-6">
          <div className="border border-luxa-sand bg-luxa-shell p-6">
            <p className="overline text-luxa-espresso/45">Revenue by month</p>
            <div className="mt-6 h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats?.monthly || []}>
                  <XAxis dataKey="month" stroke="#2C1E16" fontSize={11} />
                  <YAxis stroke="#2C1E16" fontSize={11} />
                  <Tooltip />
                  <Bar dataKey="revenue" fill="#5C3A21" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-8 flex flex-wrap gap-8">
              {Object.entries(stats?.by_status || {}).map(([k, v]) => (
                <div key={k}><p className="overline text-luxa-espresso/45">{k}</p><p className="font-serif text-2xl mt-1">{v}</p></div>
              ))}
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
